import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    Mic, 
    FileText, 
    Play, 
    Loader2, 
    Code, 
    Users, 
    Monitor, 
    FileBox, 
    ChevronRight, 
    CheckCircle2, 
    AlertCircle, 
    Copy, 
    Check,
    History,
    Upload
} from 'lucide-react';
import api from '../../../../api/client';
import NotificationPopup from '../../component/NotificationPopup';
import PremiumSelect from '../../component/PremiumSelect';

const PracticeInterviewPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [jobDescription, setJobDescription] = useState('');
    const [inputMode, setInputMode] = useState('paste'); // 'paste' | 'upload'
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [selectedType, setSelectedType] = useState('technical');
    const [experience, setExperience] = useState('Fresher');
    const [role, setRole] = useState('');
    const [includeCoding, setIncludeCoding] = useState(false);
    
    const [loading, setLoading] = useState(false);
    const [fetchingInterviews, setFetchingInterviews] = useState(true);
    const [recentInterviews, setRecentInterviews] = useState([]);
    
    const [sessionData, setSessionData] = useState(null);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [copySuccess, setCopySuccess] = useState(false);

    // Fetch candidate's active resume to bind the session
    const [resumeId, setResumeId] = useState(null);

    const interviewTypes = [
        { id: 'technical', label: t('b2cDashboard.types.technical', 'Technical'), sub: t('b2cDashboard.types.technicalSub', 'Coding, DSA, CS Concepts'), icon: <Code className="w-5 h-5" />, color: 'text-purple-600', bg: 'bg-purple-100', border: 'border-purple-200', activeBg: 'bg-purple-50', activeBorder: 'border-purple-600' },
        { id: 'hr', label: t('b2cDashboard.types.hr', 'HR Interview'), sub: t('b2cDashboard.types.hrSub', 'HR questions and company fit'), icon: <Users className="w-5 h-5" />, color: 'text-pink-600', bg: 'bg-pink-100', border: 'border-pink-200', activeBg: 'bg-pink-50', activeBorder: 'border-pink-600' },
        { id: 'behavioral', label: t('b2cDashboard.types.behavioral', 'Behavioral'), sub: t('b2cDashboard.types.behavioralSub', 'Situational & behavioral'), icon: <Users className="w-5 h-5" />, color: 'text-teal-600', bg: 'bg-teal-100', border: 'border-teal-200', activeBg: 'bg-teal-50', activeBorder: 'border-teal-600' },
        { id: 'system_design', label: t('b2cDashboard.types.system_design', 'System Design'), sub: t('b2cDashboard.types.system_designSub', 'Architecture & system design'), icon: <Monitor className="w-5 h-5" />, color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-200', activeBg: 'bg-orange-50', activeBorder: 'border-orange-600' },
        { id: 'case_study', label: t('b2cDashboard.types.case_study', 'Case Study'), sub: t('b2cDashboard.types.case_studySub', 'Business case & problem solving'), icon: <FileBox className="w-5 h-5" />, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200', activeBg: 'bg-blue-50', activeBorder: 'border-blue-600' },
    ];

    const experienceLevels = [
        { value: 'Fresher', label: t('b2cDashboard.experienceLevels.fresher', 'Fresher (0 years)') },
        { value: 'Junior', label: t('b2cDashboard.experienceLevels.junior', 'Junior (1-3 years)') },
        { value: 'Mid-Level', label: t('b2cDashboard.experienceLevels.mid', 'Mid-Level (3-5 years)') },
        { value: 'Senior', label: t('b2cDashboard.experienceLevels.senior', 'Senior (5-8 years)') },
        { value: 'Lead/Manager', label: t('b2cDashboard.experienceLevels.lead', 'Lead/Manager (8+ years)') },
    ];

    useEffect(() => {
        const initData = async () => {
            try {
                // Fetch resumes
                const resumeRes = await api.get('resumes/');
                const resumeList = Array.isArray(resumeRes.data?.results) ? resumeRes.data.results : (Array.isArray(resumeRes.data) ? resumeRes.data : []);
                const activeResume = resumeList.find((r) => r.is_active || r.is_default) || resumeList[0];
                if (activeResume) {
                    setResumeId(activeResume.id);
                }

                // Fetch recent interviews
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const candidateId = user.id || user.user_id;
                if (candidateId) {
                    const candidateRes = await api.get(`/candidates/${candidateId}/?interview_page=1&interview_page_size=3`);
                    const interviews = candidateRes.data?.interviews?.results || [];
                    setRecentInterviews(interviews);
                }
            } catch (err) {
                console.error("Failed to fetch initial data:", err);
            } finally {
                setFetchingInterviews(false);
            }
        };
        initData();
    }, []);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset input
        if (fileInputRef.current) fileInputRef.current.value = '';

        const allowedTypes = ['.pdf', '.docx', '.doc', '.txt'];
        const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedTypes.includes(fileExt)) {
            setSnackbar({ open: true, message: t('b2cDashboard.invalidFileType', 'Invalid file type. Please upload a PDF, DOCX, or TXT file.'), severity: 'error' });
            return;
        }

        setIsUploading(true);
        setError('');
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await api.post('job-descriptions/extract-text-from-file/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data?.extracted_text) {
                setJobDescription(response.data.extracted_text);
                setSnackbar({ open: true, message: t('b2cDashboard.textExtractedSuccess', 'Text extracted successfully!'), severity: 'success' });
                // Switch back to paste mode to let user review
                setInputMode('paste');
            } else {
                throw new Error('Failed to extract text from file.');
            }
        } catch (err) {
            console.error('Error extracting text from JD file:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Failed to extract text. Please try again or paste manually.';
            setError(errorMessage);
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleGenerateMock = async () => {
        if (!resumeId) {
            setError(t('b2cDashboard.pleaseUploadResume', 'Please upload a resume in your profile to start practice interviews.'));
            setSnackbar({ open: true, message: t('b2cDashboard.resumeRequiredError', 'Resume required to start interview.'), severity: 'error' });
            return;
        }
        if (!jobDescription.trim()) {
            setError(t('b2cDashboard.pleasePasteJd', 'Please paste a Job Description.'));
            return;
        }
        if (!role.trim()) {
            setError(t('b2cDashboard.pleaseEnterRole', 'Please enter a target role.'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Using existing interview session API endpoint, passing JD and preferences
            const response = await api.post('job-applications/{application_pk}/create_interview_session/', {
                resume_id: resumeId,
                practice_session: true,
                job_description: jobDescription,
                experience_level: experience,
                interview_type: selectedType,
                target_role: role,
                include_coding: includeCoding,
                language: (i18n.language || 'en').split('-')[0],
                // Passing JD directly as skills for backend to parse if it supports it, 
                // or just sending it directly.
                selected_skills: [role, selectedType] 
            });

            const newSessionData = { token: response.data.token };
            setSessionData(newSessionData);
            setSnackbar({ open: true, message: t('b2cDashboard.mockPreparedSuccess', 'Mock interview prepared successfully!'), severity: 'success' });
        } catch (err) {
            console.error('Error generating mock interview:', err);
            const errorMessage = err.response?.data?.error || 'Failed to generate interview. Please try again.';
            setError(errorMessage);
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!sessionData) return;
        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_INTERVIEW_UI_URL || window.location.origin;
            const interviewLink = `${baseUrl}/interview/invite/${sessionData.token}`;
            await navigator.clipboard.writeText(interviewLink);
            setCopySuccess(true);
            setSnackbar({ open: true, message: t('b2cDashboard.linkCopiedClipboard', 'Link copied to clipboard!'), severity: 'success' });
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (err) {
            setError(t('b2cDashboard.failedCopyLink', 'Failed to copy interview link.'));
        } finally {
            setLoading(false);
        }
    };

    const handleStartInterview = () => {
        if (!sessionData) return;
        const baseUrl = process.env.REACT_APP_INTERVIEW_UI_URL || window.location.origin;
        window.open(`${baseUrl}/interview/invite/${sessionData.token}`, '_blank');
    };

    return (
        <div className="p-4 sm:p-6 w-full min-h-screen overflow-hidden" style={{ background: '#f8fafc', fontFamily: "'Poppins', sans-serif" }}>
            <NotificationPopup open={snackbar.open} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} message={snackbar.message} />
            
            {/* ── Overview Header ────── */}
            <div className="mb-8">
                <div
                    className="w-full px-4 py-3 rounded-xl text-white"
                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex-1 min-w-60">
                            <h1 className="text-lg font-semibold mb-0.5 text-white">
                                {t('b2cDashboard.practiceInterview', 'Practice Interview')}
                            </h1>
                            <p className="text-xs text-blue-100">
                                {t('b2cDashboard.pasteJdDescription', 'Paste a Job Description to generate a highly targeted mock interview.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* Main Action Area */}
                <div className="lg:col-span-2 space-y-6">
                    
                    {/* JD Input Card */}
                    <div className="bg-white rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-gray-100 p-6 sm:p-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                                {t('b2cDashboard.provideJd', 'Provide Job Description')}
                            </h2>
                        </div>
                        
                        {/* Tabs */}
                        <div className="flex space-x-2 mb-6 p-1 bg-gray-50 rounded-xl inline-flex">
                            <button
                                onClick={() => !sessionData && setInputMode('paste')}
                                disabled={!!sessionData}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center ${
                                    inputMode === 'paste' 
                                    ? 'bg-white text-indigo-600 shadow-sm border border-gray-200' 
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                } ${sessionData ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <FileText className="w-4 h-4 mr-1.5" />
                                {t('b2cDashboard.pasteText', 'Paste Text')}
                            </button>
                            <button
                                onClick={() => !sessionData && setInputMode('upload')}
                                disabled={!!sessionData}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center ${
                                    inputMode === 'upload' 
                                    ? 'bg-white text-indigo-600 shadow-sm border border-gray-200' 
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                } ${sessionData ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <Upload className="w-4 h-4 mr-1.5" />
                                {t('b2cDashboard.uploadFile', 'Upload File')}
                            </button>
                        </div>

                        {inputMode === 'paste' ? (
                            <>
                                <p className="text-sm text-gray-500 mb-4">
                                    {t('b2cDashboard.copyPasteJdHint', 'Copy and paste the entire job description text below. Our AI will analyze the requirements, skills, and tone to construct the perfect mock interview.')}
                                </p>
                                <textarea
                                    value={jobDescription}
                                    onChange={(e) => setJobDescription(e.target.value)}
                                    placeholder={t('b2cDashboard.jdPlaceholder', 'e.g. We are looking for a Senior Software Engineer with 5+ years of experience in React and Node.js...')}
                                    className="w-full h-48 p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 text-sm resize-none font-medium transition-all"
                                    disabled={!!sessionData}
                                ></textarea>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-gray-500 mb-4">
                                    {t('b2cDashboard.uploadJdHint', 'Upload the Job Description file using the uploader below. Our AI will analyze the requirements, skills, and tone to construct the perfect mock interview.')}
                                </p>
                                <div 
                                className={`w-full h-48 bg-gray-50 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center transition-all ${
                                    isUploading ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-100 cursor-pointer'
                                }`}
                                onClick={() => !isUploading && !sessionData && fileInputRef.current?.click()}
                            >
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleFileUpload} 
                                    className="hidden" 
                                    accept=".pdf,.docx,.doc,.txt"
                                    disabled={isUploading || !!sessionData}
                                />
                                {isUploading ? (
                                    <div className="flex flex-col items-center text-indigo-600">
                                        <Loader2 className="w-8 h-8 animate-spin mb-3" />
                                        <span className="font-semibold text-sm">{t('b2cDashboard.extractingText', 'Extracting text...')}</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center text-gray-500">
                                        <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3">
                                            <Upload className="w-6 h-6 text-indigo-500" />
                                        </div>
                                        <span className="font-bold text-gray-700 mb-1">{t('b2cDashboard.clickToUploadJd', 'Click to upload JD')}</span>
                                        <span className="text-xs text-gray-400">{t('b2cDashboard.supportedFormats', 'PDF, DOCX, or TXT')}</span>
                                    </div>
                                )}
                            </div>
                            </>
                        )}
                    </div>

                    {/* Preferences Card */}
                    <div className="bg-white rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-gray-100 p-6 sm:p-8">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">{t('b2cDashboard.interviewPreferences', 'Interview Preferences')}</h2>
                        
                        {/* Interview Types */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                            {interviewTypes.map((type) => (
                                <div 
                                    key={type.id}
                                    onClick={() => !sessionData && setSelectedType(type.id)}
                                    className={`relative p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                                        selectedType === type.id 
                                        ? `${type.activeBorder} ${type.activeBg} shadow-sm` 
                                        : `border-gray-100 hover:border-gray-300 hover:bg-gray-50`
                                    } ${sessionData ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {selectedType === type.id && (
                                        <div className={`absolute top-3 right-3 ${type.color}`}>
                                            <CheckCircle2 className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${type.bg} ${type.color}`}>
                                        {type.icon}
                                    </div>
                                    <div className="font-bold text-gray-900 text-sm mb-1">{type.label}</div>
                                    <div className="text-xs text-gray-500">{type.sub}</div>
                                </div>
                            ))}
                        </div>

                        {/* Dropdowns */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('b2cDashboard.targetRole', 'Target Role')}</label>
                                <input
                                    type="text"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    placeholder={t('b2cDashboard.rolePlaceholder', 'e.g. Frontend Developer')}
                                    disabled={!!sessionData}
                                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 font-medium"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('b2cDashboard.experienceLevel', 'Experience Level')}</label>
                                <PremiumSelect
                                    variant="b2c"
                                    value={experience}
                                    onChange={setExperience}
                                    options={experienceLevels}
                                    disabled={!!sessionData}
                                />
                            </div>
                        </div>

                        {/* Toggles */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div>
                                <div className="font-bold text-gray-900 text-sm">{t('b2cDashboard.includeCoding', 'Include Coding Questions')}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{t('b2cDashboard.includeCodingHint', 'Add interactive coding IDE rounds to the interview.')}</div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={includeCoding} onChange={(e) => !sessionData && setIncludeCoding(e.target.checked)} disabled={!!sessionData} />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                            </label>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl flex items-center shadow-sm">
                            <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
                            <span className="font-medium text-sm">{error}</span>
                        </div>
                    )}

                    {/* Action Area */}
                    <div className="flex flex-col sm:flex-row justify-end items-center gap-4 pt-4">
                        {!sessionData ? (
                            <div className="flex flex-col items-end w-full sm:w-auto">
                                <button
                                    onClick={handleGenerateMock}
                                    disabled={loading || !jobDescription.trim() || !role.trim()}
                                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold px-8 py-3.5 rounded-xl shadow-[0_4px_14px_0_rgba(99,102,241,0.39)] hover:shadow-[0_6px_20px_rgba(99,102,241,0.23)] transition-all flex items-center justify-center space-x-2"
                                >
                                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
                                    <span>{loading ? t('b2cDashboard.analyzingJd', 'Analyzing JD...') : t('b2cDashboard.generateMock', 'Generate Mock Interview')}</span>
                                </button>
                                <div className="text-[11px] font-semibold text-gray-500 mt-2 flex items-center mr-1">
                                    <div className="w-3.5 h-3.5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mr-1.5">
                                        <span className="text-[9px]">⚡</span>
                                    </div>
                                    {t('b2cDashboard.oneCreditUsed', '1 Credit will be used')}
                                </div>
                            </div>
                        ) : (
                            <div className="flex w-full sm:w-auto gap-3">
                                <button
                                    onClick={() => {
                                        setSessionData(null);
                                        setJobDescription('');
                                    }}
                                    className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-bold px-6 py-3.5 rounded-xl shadow-sm transition-colors flex items-center justify-center w-full sm:w-auto text-sm"
                                >
                                    {t('b2cDashboard.startOver', 'Start Over')}
                                </button>
                                <button
                                    onClick={handleCopyLink}
                                    className="bg-white hover:bg-gray-50 text-indigo-600 border border-indigo-200 font-bold px-6 py-3.5 rounded-xl shadow-sm transition-colors flex items-center justify-center w-full sm:w-auto text-sm"
                                >
                                    {copySuccess ? <Check className="w-5 h-5 mr-2" /> : <Copy className="w-5 h-5 mr-2" />}
                                    {copySuccess ? t('b2cDashboard.copied', 'Copied!') : t('b2cDashboard.copyLink', 'Copy Link')}
                                </button>
                                <button
                                    onClick={handleStartInterview}
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-3.5 rounded-xl shadow-[0_4px_14px_0_rgba(22,163,74,0.39)] transition-all flex items-center justify-center w-full sm:w-auto"
                                >
                                    <Play className="w-5 h-5 mr-2" />
                                    {t('b2cDashboard.startInterview', 'Start Interview')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column: History & Tips */}
                <div className="space-y-6">

                    {/* Features/Tips */}
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl border border-indigo-100 p-6">
                        <h3 className="text-lg font-bold text-indigo-900 mb-4">{t('b2cDashboard.whyJdPractice', 'Why use JD Practice?')}</h3>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-indigo-600 mr-3 shrink-0 mt-0.5" />
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{t('b2cDashboard.highlyTargeted', 'Highly Targeted Questions')}</div>
                                    <div className="text-xs text-gray-600 mt-0.5">{t('b2cDashboard.highlyTargetedDesc', 'AI extracts key requirements from the JD to ask hyper-relevant questions.')}</div>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-indigo-600 mr-3 shrink-0 mt-0.5" />
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{t('b2cDashboard.realisticExperience', 'Realistic Experience')}</div>
                                    <div className="text-xs text-gray-600 mt-0.5">{t('b2cDashboard.realisticExperienceDesc', 'Get a feel for the actual interview difficulty based on the specific company.')}</div>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-indigo-600 mr-3 shrink-0 mt-0.5" />
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{t('b2cDashboard.detailedFeedback', 'Detailed Feedback')}</div>
                                    <div className="text-xs text-gray-600 mt-0.5">{t('b2cDashboard.detailedFeedbackDesc', 'Identify gaps between your resume and the job description.')}</div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PracticeInterviewPage;
